import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Check } from 'lucide-react';
import { IntegrationProvider } from '@momentum/types';
import { cn } from '@/shared/utils/utils';
import { useNangoConnect } from '@/shared/hooks/useNangoConnect';
import { FullPageCta } from '@/shared/components/FullPageCta';
import {
  PROVIDER_CARDS,
  ProviderCardConfig,
  ProviderI18nKey,
} from './providerCards.config';

interface AdminIntegrationsScreenProps {
  connectedProviders: IntegrationProvider[];
  onContinue: () => void;
}

export function AdminIntegrationsScreen({
  connectedProviders,
  onContinue,
}: AdminIntegrationsScreenProps) {
  const { t } = useTranslation('onboarding');

  const { connect } = useNangoConnect({});
  const [connectingKey, setConnectingKey] = useState<ProviderI18nKey | null>(
    null
  );

  const connectedSet = useMemo<Set<IntegrationProvider>>(
    () => new Set(connectedProviders),
    [connectedProviders]
  );
  const connectedCount = connectedSet.size;
  const canContinue = connectedCount > 0;

  const handleConnect = (config: ProviderCardConfig) => {
    if (!config.live || !config.providerConfigKey) return;
    if (connectingKey !== null) return;
    setConnectingKey(config.i18nKey);
    void connect({ providerConfigKey: config.providerConfigKey }).finally(() =>
      setConnectingKey(null)
    );
  };

  return (
    <div className="relative flex-1 flex flex-col">
      <div className="mx-auto w-full max-w-5xl px-6 sm:px-10 pt-6 pb-44">
        <header className="text-center mb-12">
          <span className="text-[11px] font-medium uppercase tracking-[0.32em] text-muted-foreground">
            {t('admin.integrations.stepBadge')}
          </span>
          <h1 className="mt-5 text-balance text-4xl sm:text-6xl font-bold leading-[1.05] tracking-tight text-foreground">
            {t('admin.integrations.heading')}
          </h1>
          <div className="mt-7 flex flex-col items-center gap-5">
            <div aria-hidden className="h-px w-12 bg-foreground/20" />
            <p className="max-w-2xl text-balance text-base sm:text-lg text-muted-foreground leading-relaxed">
              {t('admin.integrations.subheading')}
            </p>
          </div>
        </header>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {PROVIDER_CARDS.map((card) => {
            const Logo = card.logo;
            const isConnected =
              !!card.providerEnum && connectedSet.has(card.providerEnum);
            const isDisabled = !card.live;

            return (
              <article
                key={card.i18nKey}
                className={cn(
                  'group relative flex flex-col rounded-2xl border p-6 shadow-sm backdrop-blur-sm transition-all',
                  isConnected &&
                    'border-emerald-500/40 bg-emerald-50/40 shadow-[0_10px_30px_-15px_rgba(16,185,129,0.35)]',
                  !isConnected && !isDisabled && 'bg-white/75',
                  !isConnected &&
                    isDisabled &&
                    'bg-slate-100/40 opacity-70 saturate-[0.85]',
                  !isConnected && card.tint,
                  !isConnected &&
                    !isDisabled &&
                    'hover:-translate-y-0.5 hover:shadow-md hover:bg-white/90'
                )}
              >
                <div className="flex items-start gap-4">
                  <div
                    className={cn(
                      'relative shrink-0 h-14 w-14 rounded-xl ring-1 ring-inset flex items-center justify-center transition-transform duration-300',
                      isConnected
                        ? 'bg-emerald-500/10 ring-emerald-500/20'
                        : card.logoTint,
                      !isConnected && !isDisabled && 'group-hover:scale-[1.04]'
                    )}
                  >
                    <Logo />
                    {isConnected && (
                      <span
                        aria-hidden
                        className="absolute -bottom-1 -end-1 h-6 w-6 rounded-full bg-emerald-500 ring-2 ring-card flex items-center justify-center shadow-sm"
                      >
                        <Check className="h-3 w-3 text-white" />
                      </span>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-foreground text-base leading-tight tracking-tight">
                      {t(`admin.integrations.providers.${card.i18nKey}.name`)}
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">
                      {t(
                        `admin.integrations.providers.${card.i18nKey}.description`
                      )}
                    </p>
                  </div>
                </div>

                <div
                  className={cn(
                    'mt-auto pt-4 border-t flex items-center justify-end gap-3',
                    isConnected
                      ? 'border-emerald-500/15'
                      : 'border-foreground/[0.06]'
                  )}
                >
                  {isConnected && (
                    <span className="text-xs font-semibold text-emerald-700">
                      {t('admin.integrations.connected')}
                    </span>
                  )}
                  {!isConnected && isDisabled && (
                    <span className="text-xs font-medium text-muted-foreground">
                      {t('admin.integrations.comingSoonHint')}
                    </span>
                  )}
                  {!isConnected && !isDisabled && (
                    <FullPageCta
                      variant="primary"
                      disabled={connectingKey === card.i18nKey}
                      onClick={() => handleConnect(card)}
                      className="px-5 py-2 text-xs"
                    >
                      {connectingKey === card.i18nKey
                        ? t('admin.integrations.connecting')
                        : t('admin.integrations.connect')}
                    </FullPageCta>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </div>

      <div className="sticky bottom-0 mt-auto w-full border-t border-foreground/[0.06] bg-page-bg/95 backdrop-blur-xl shadow-[0_-8px_24px_-12px_rgba(15,23,42,0.08)]">
        <div className="mx-auto max-w-5xl px-6 sm:px-10 py-4 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="inline-flex items-center gap-2 text-sm font-medium text-foreground/80">
            {canContinue && (
              <span
                aria-hidden
                className="inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500"
              />
            )}
            {t('admin.integrations.connectedCount', {
              count: connectedCount,
            })}
          </div>
          <div className="flex flex-col items-center sm:items-end gap-1.5">
            <FullPageCta
              variant="primary"
              onClick={onContinue}
              disabled={!canContinue}
              title={
                !canContinue ? t('admin.integrations.softGateHint') : undefined
              }
            >
              {t('admin.integrations.continue')}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
            </FullPageCta>
            <p className="text-xs text-muted-foreground">
              {canContinue
                ? t('admin.integrations.nudgeMore')
                : t('admin.integrations.softGateHint')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
